'use client';

import React, {
  ReactEventHandler,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import FilePresentIcon from '@mui/icons-material/FilePresentRounded';
import {
  isEmptyString,
  getFileExtension,
} from 'utils/helper';
import {
  downloadFromUrl,
  getIsExtensionDisplayableInIFrameTag,
  getIsExtensionDisplayableInImgTag,
  printPdfFromUrl,
} from 'utils/files';
import Button from 'components/common/button';
import Image from 'components/common/image';
import DeleteIcon from 'assets/svg/delete';
import ToolTip from 'components/common/tooltip';
import ZoomIcon from 'assets/svg/zoom';
import DownloadIcon from 'assets/svg/download';
import { MediaOriginatorType } from 'utils/constants';
import usePreviewMediaClick from 'hooks/usePreviewMediaClick';
import useDeleteMediaHandler from 'hooks/useDeleteMediaHandler';
import PrintIcon from '@mui/icons-material/Print';
import isNil from 'lodash/isNil';
import Spinner from 'components/spinner/Spinner';
import { MultiMediaResponse, MultiMediaDeleteDTO } from 'artifacts/dtos';

  type Props = {
    media: MultiMediaResponse;
    patientId?: number;
    isFullScreen?: boolean;
    fullSize?: boolean;
    thumbnail?: boolean;
    className?: string;
    hasDelete?: boolean;
    hasPrint?: boolean;
    hasDownload?: boolean;
    showToolbar?: boolean;
    onDelete?: (multimedia: MultiMediaDeleteDTO) => void | Promise<void>;
    refetch?: () => Promise<void>;
    onLoad?: ReactEventHandler<HTMLIFrameElement>;
  };
export default function MediaPreview({
  media,
  isFullScreen = false,
  thumbnail = false,
  patientId = 0,
  className,
  fullSize = false,
  hasDelete = true,
  hasPrint = false,
  hasDownload = false,
  showToolbar = true,
  onDelete: _onDelete,
  refetch,
  onLoad,
}: Props) {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const extension = getFileExtension(media?.fileName ?? '') ?? '';
  const isDisplayableDocument = getIsExtensionDisplayableInIFrameTag(extension);
  const displayable = isDisplayableDocument || getIsExtensionDisplayableInImgTag(extension);

  const url = media.url?.value ?? '';
  const view = isFullScreen ? 'FitH' : 'Fit';
  const toolbar = showToolbar && isFullScreen;
  const src = useMemo(() => (
    !displayable ? undefined : `${url}#toolbar=${Number(toolbar)}&view=${view}`
  ), [displayable, url, isFullScreen, view]);

  const onPreviewClick = usePreviewMediaClick();
  const { onMediaDelete } = useDeleteMediaHandler(refetch);
  const onDelete = typeof _onDelete === 'function' ? _onDelete : onMediaDelete;

  const PreviewTag = isDisplayableDocument ? 'iframe' : Image;

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadFromUrl(url, media.fileName ?? 'download');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={clsx('group/thumbnail flex-col justify-center w-full h-full min-h-[80px] relative flex items-center cursor-pointer overflow-hidden', className)}>
      <PreviewTag
        key={Number(isFullScreen)}
        onLoad={onLoad}
        title={media?.fileName ?? ''}
        className={clsx('border-none', {
          '!w-full h-full': isDisplayableDocument,
          '!w-full !h-auto max-h-full': !isDisplayableDocument && !fullSize,
          '!h-auto !w-auto': fullSize && !isDisplayableDocument,
        })}
        src={src}
        variant="rounded"
        size="fit"
        color={!thumbnail || !isEmptyString(src) ? 'transparent' : '#9ca3af'}
      >
        {(thumbnail && !displayable) && (
          <div className="flex flex-col items-center p-2">
            <FilePresentIcon className="text-white !w-12 !h-12" />
            <p className="text-center text-xs">{media.fileName}</p>
          </div>
        )}
        {!thumbnail && !displayable && (
          <div className="w-full text-center flex flex-col items-center justify-center">
            <p className="p-4 text-gray-500 text-sm">
              Preview is not available for this document.
            </p>
            <Button
              variant="contained"
              color="primary"
              href={url}
            >
              <DownloadIcon className="mr-2 !w-4 !h-4 text-white" />
              Download
            </Button>
          </div>
        )}
      </PreviewTag>
      {thumbnail && (
        <div className={clsx(
          'absolute transition-opacity opacity-0 top-0 inset-x-0 w-full !h-8',
          'group-hover/thumbnail:opacity-100 group-hover/thumbnail:bg-black group-hover/thumbnail:bg-opacity-40',
        )}
        >
          <div className="space-x-3 flex h-full items-center justify-end !w-full cursor-pointer p-2">
            {!isNil(url) && hasDownload && (
              <ToolTip title="Download">
                <span>
                  {isDownloading ? (
                    <span className="-mt-0.5 flex items-center justify-center !text-white">
                      <Spinner
                        name="MoonLoader"
                        size={15}
                        color="#fff"
                      />
                    </span>
                  ) : (
                    <DownloadIcon
                      onClick={handleDownload}
                      className="-mt-0.5 !text-lg !text-white !empty-fill"
                    />
                  )}
                </span>
              </ToolTip>
            )}
            {!isNil(url) && hasPrint && (
              <ToolTip title="Print">
                <span>
                  <PrintIcon
                    onClick={() => printPdfFromUrl(url)}
                    className="!text-lg !text-white !empty-fill"
                  />
                </span>
              </ToolTip>
            )}
            <ToolTip title="Open Preview">
              <span>
                <ZoomIcon
                  onClick={() => onPreviewClick(media)}
                  className="!text-lg !text-white !empty-fill"
                />
              </span>
            </ToolTip>
            {hasDelete && (
            <ToolTip title="Delete">
              <span>
                <DeleteIcon
                  onClick={() => {
                    onDelete(
                      {
                        list:
                            [
                              {
                                originatorType: MediaOriginatorType.Patient,
                                mediaId: media.mediaId ?? 0,
                              },
                            ],
                      },
                    );
                  }}
                  className="mt-1 !w-5 !h-5 !text-white empty-fill"
                />
              </span>
            </ToolTip>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
